import type { Course, AppSettings } from '@gpa-scales/shared-types';
import { secureSave, secureLoad, secureRemove } from '@/utils/secureStorage';

const PROFILES_KEY = 'gpa-scales_profiles';
const ACTIVE_PROFILE_KEY = 'gpa-scales_active_profile';

export interface StudentProfile {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

function generateProfileId(): string {
  return `profile_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function getProfileKey(profileId: string): string {
  return `gpa-scales_data_${profileId}`;
}

async function saveProfiles(profiles: StudentProfile[]): Promise<void> {
  await secureSave(PROFILES_KEY, profiles);
}

/**
 * Get the list of all profiles.
 */
export async function listProfiles(): Promise<StudentProfile[]> {
  return (await secureLoad<StudentProfile[]>(PROFILES_KEY)) ?? [];
}

/**
 * Get the currently active profile ID.
 */
export async function getActiveProfileId(): Promise<string | null> {
  return (await secureLoad<string>(ACTIVE_PROFILE_KEY)) ?? null;
}

/**
 * Create a new student profile.
 * Returns the new profile object.
 */
export async function createProfile(name: string): Promise<StudentProfile> {
  const profiles = await listProfiles();
  const profile: StudentProfile = {
    id: generateProfileId(),
    name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  profiles.push(profile);
  await saveProfiles(profiles);
  return profile;
}

/**
 * Save current data to a specific profile's namespace.
 */
export async function saveProfileData(
  profileId: string,
  courses: Course[],
  settings: AppSettings
): Promise<void> {
  const key = getProfileKey(profileId);
  const data = { courses, settings, updatedAt: new Date().toISOString() };
  await secureSave(key, data);
}

/**
 * Load a specific profile's data.
 */
export async function loadProfileData(
  profileId: string
): Promise<{ courses: Course[]; settings: AppSettings } | null> {
  const data = await secureLoad<{ courses: Course[]; settings: AppSettings }>(getProfileKey(profileId));
  if (!data) return null;
  return { courses: data.courses, settings: data.settings };
}

/**
 * Switch to a different profile.
 * 1. Saves current data to the current profile (if one is active)
 * 2. Loads the target profile's data
 * 3. Updates the active profile indicator
 *
 * Returns the loaded profile data, or null if the profile has no saved data yet.
 */
export async function switchProfile(
  newProfileId: string,
  currentCourses: Course[],
  currentSettings: AppSettings
): Promise<{ courses: Course[]; settings: AppSettings } | null> {
  // Save current data to current profile
  const currentId = await getActiveProfileId();
  if (currentId) {
    await saveProfileData(currentId, currentCourses, currentSettings);
  }

  // Load new profile data
  const data = await loadProfileData(newProfileId);

  // Set active profile
  await secureSave(ACTIVE_PROFILE_KEY, newProfileId);

  return data;
}

/**
 * Delete a profile and its associated data.
 */
export async function deleteProfile(id: string): Promise<void> {
  // Remove profile data
  await secureRemove(getProfileKey(id));

  // Remove from profiles list
  const profiles = (await listProfiles()).filter((p) => p.id !== id);
  await saveProfiles(profiles);

  // If this was the active profile, clear the active indicator
  if ((await getActiveProfileId()) === id) {
    await secureRemove(ACTIVE_PROFILE_KEY);
  }
}

/**
 * Rename a profile.
 */
export async function renameProfile(id: string, name: string): Promise<void> {
  const profiles = await listProfiles();
  const profile = profiles.find((p) => p.id === id);
  if (profile) {
    profile.name = name;
    profile.updatedAt = new Date().toISOString();
    await saveProfiles(profiles);
  }
}

/**
 * Initialize the default profile if none exists.
 * Returns the default profile.
 */
export async function ensureDefaultProfile(): Promise<StudentProfile> {
  const profiles = await listProfiles();
  if (profiles.length === 0) {
    const defaultProfile = await createProfile('Student 1');
    await secureSave(ACTIVE_PROFILE_KEY, defaultProfile.id);
    return defaultProfile;
  }
  const activeId = await getActiveProfileId();
  if (!activeId) {
    const first = profiles[0];
    await secureSave(ACTIVE_PROFILE_KEY, first.id);
    return first;
  }
  const active = profiles.find((p) => p.id === activeId);
  return active ?? profiles[0];
}
